import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaShieldAlt, 
  FaLock, 
  FaUserShield, 
  FaDatabase, 
  FaServer, 
  FaGlobe,
  FaMobileAlt,
  FaDesktop,
  FaTabletAlt,
  FaRocket,
  FaChartLine,
  FaUsers,
  FaCog,
  FaCheckCircle,
  FaStar,
  FaAward,
  FaHeadset,
  FaDownload,
  FaCloud,
  FaSync,
  FaEye,
  FaEyeSlash,
  FaFingerprint,
  FaQrcode
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const LearnMore = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const features = [
    {
      icon: <FaShieldAlt className="text-4xl" />,
      title: "Bank-Grade Security",
      description: "Enterprise-level encryption, multi-factor authentication, and real-time fraud detection to protect your financial data.",
      details: [
        "256-bit AES encryption",
        "Biometric authentication",
        "Real-time fraud monitoring",
        "Secure SSL/TLS connections"
      ],
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: <FaUsers className="text-4xl" />,
      title: "Role-Based Access Control",
      description: "Comprehensive user management with Admin, Distributor, and User roles with specialized permissions and sub-roles.",
      details: [
        "Multi-level user hierarchy",
        "Granular permissions",
        "Audit trail logging",
        "Session management"
      ],
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: <FaChartLine className="text-4xl" />,
      title: "Real-Time Analytics",
      description: "Advanced analytics and reporting with live data visualization, customizable dashboards, and automated insights.",
      details: [
        "Live data visualization",
        "Customizable dashboards",
        "Automated reporting",
        "Performance metrics"
      ],
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      icon: <FaMobileAlt className="text-4xl" />,
      title: "Multi-Platform Support",
      description: "Seamless access across web, Android, and iOS platforms with synchronized data and offline capabilities.",
      details: [
        "Native mobile apps",
        "Offline functionality",
        "Cross-platform sync",
        "Push notifications"
      ],
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50"
    }
  ];

  const securityFeatures = [
    {
      icon: <FaLock className="text-3xl" />,
      title: "End-to-End Encryption",
      description: "All data is encrypted in transit and at rest using industry-standard encryption protocols."
    },
    {
      icon: <FaUserShield className="text-3xl" />,
      title: "Multi-Factor Authentication",
      description: "Enhanced security with SMS, email, and biometric authentication options."
    },
    {
      icon: <FaDatabase className="text-3xl" />,
      title: "Secure Data Centers",
      description: "Data stored in ISO 27001 certified data centers with redundant backup systems."
    },
    {
      icon: <FaServer className="text-3xl" />,
      title: "Regular Security Audits",
      description: "Continuous security monitoring and regular penetration testing by certified experts."
    }
  ];

  const platforms = [
    {
      icon: <FaDesktop className="text-4xl" />,
      title: "Web Application",
      description: "Modern React-based web interface with responsive design and real-time updates.",
      features: ["Responsive Design", "Real-time Updates", "Cross-browser Support", "Progressive Web App"],
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: <FaMobileAlt className="text-4xl" />,
      title: "Android App",
      description: "Native Android application with offline capabilities and push notifications.",
      features: ["Offline Mode", "Push Notifications", "Native Performance", "Biometric Auth"],
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: <FaTabletAlt className="text-4xl" />,
      title: "iOS App",
      description: "Native iOS application with seamless integration and App Store optimization.",
      features: ["iOS Native", "Face ID/Touch ID", "App Store Ready", "iCloud Sync"],
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50"
    }
  ];

  const benefits = [
    {
      icon: <FaRocket className="text-3xl" />,
      title: "High Performance",
      description: "Optimized architecture ensures fast loading times and smooth user experience."
    },
    {
      icon: <FaCloud className="text-3xl" />,
      title: "Scalable Infrastructure",
      description: "Cloud-based infrastructure that scales automatically with your business needs."
    },
    {
      icon: <FaSync className="text-3xl" />,
      title: "Real-time Sync",
      description: "Instant synchronization across all devices and platforms."
    },
    {
      icon: <FaHeadset className="text-3xl" />,
      title: "24/7 Support",
      description: "Round-the-clock customer support with multiple contact channels."
    }
  ];

  const stats = [
    { number: "99.9%", label: "Uptime Guarantee", icon: <FaServer /> },
    { number: "10K+", label: "Active Users", icon: <FaUsers /> },
    { number: "50+", label: "Features", icon: <FaCog /> },
    { number: "24/7", label: "Support", icon: <FaHeadset /> }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FaGlobe /> },
    { id: 'security', label: 'Security', icon: <FaShieldAlt /> },
    { id: 'features', label: 'Features', icon: <FaCog /> },
    { id: 'platforms', label: 'Platforms', icon: <FaMobileAlt /> },
    { id: 'benefits', label: 'Benefits', icon: <FaStar /> }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-12">
            {/* Hero Section */}
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Welcome to BT India
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                BT India is a comprehensive digital financial platform designed for modern India. 
                We provide secure, scalable, and user-friendly solutions for businesses and individuals 
                to manage their financial transactions efficiently.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-2xl mb-4 flex justify-center">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Key Features Preview */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Key Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {features.slice(0, 2).map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className={`${feature.bgColor} p-6 rounded-2xl border border-gray-100`}
                  >
                    <div className={`bg-gradient-to-r ${feature.color} text-white p-4 rounded-xl mb-4 flex justify-center`}>
                      {feature.icon}
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                    <p className="text-gray-600 mb-4">{feature.description}</p>
                    <ul className="space-y-2">
                      {feature.details.map((detail, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Security & Privacy
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Your security is our top priority. We implement industry-leading security measures 
                to protect your data and transactions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {securityFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
                >
                  <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-xl mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-8 rounded-3xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Security Certifications
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <FaAward className="text-4xl text-green-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900">ISO 27001</h4>
                  <p className="text-sm text-gray-600">Information Security Management</p>
                </div>
                <div className="text-center">
                  <FaShieldAlt className="text-4xl text-blue-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900">PCI DSS</h4>
                  <p className="text-sm text-gray-600">Payment Card Industry Compliance</p>
                </div>
                <div className="text-center">
                  <FaLock className="text-4xl text-purple-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900">GDPR</h4>
                  <p className="text-sm text-gray-600">Data Protection Regulation</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Powerful Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover the comprehensive features that make BT India the preferred choice 
                for digital financial management.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`${feature.bgColor} p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-300`}
                >
                  <div className={`bg-gradient-to-r ${feature.color} text-white p-6 rounded-2xl mb-6 flex justify-center`}>
                    {feature.icon}
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h4>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <FaCheckCircle className="text-green-500 mr-3 text-sm" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        );

      case 'platforms':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Multi-Platform Support
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Access BT India from anywhere, on any device. Our platform works seamlessly 
                across web, mobile, and tablet devices.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {platforms.map((platform, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`${platform.bgColor} p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-300`}
                >
                  <div className={`bg-gradient-to-r ${platform.color} text-white p-6 rounded-2xl mb-6 flex justify-center`}>
                    {platform.icon}
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">{platform.title}</h4>
                  <p className="text-gray-600 mb-6">{platform.description}</p>
                  <ul className="space-y-3">
                    {platform.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <FaCheckCircle className="text-green-500 mr-3 text-sm" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    <a
                      href="#download"
                      className={`bg-gradient-to-r ${platform.color} text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 inline-flex items-center`}
                    >
                      <FaDownload className="mr-2" />
                      Download
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        );

      case 'benefits':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose BT India?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience the advantages of choosing BT India as your digital financial partner.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-xl mb-4 flex justify-center">
                    {benefit.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h4>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-12 rounded-3xl text-center">
              <h3 className="text-3xl font-bold mb-6">Ready to Get Started?</h3>
              <p className="text-xl mb-8 text-blue-100">
                Join thousands of users who trust BT India for their financial management needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 inline-flex items-center"
                >
                  <FaRocket className="mr-2" />
                  Get Started Free
                </Link>
                <Link
                  to="/support"
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 inline-flex items-center"
                >
                  <FaHeadset className="mr-2" />
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8 }}
        className="bg-white/90 backdrop-blur-xl shadow-lg sticky top-0 z-50 border-b border-white/20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <FaRocket className="text-white text-xl" />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                BTIndia
              </h1>
            </Link>
            <Link
              to="/"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-xl transition-all duration-300 font-medium shadow-lg"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Learn More About BT India
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-4xl mx-auto">
              Discover how BT India is revolutionizing digital financial management 
              with cutting-edge technology and unparalleled security.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full">
                <FaShieldAlt className="text-green-400" />
                <span>Bank-Grade Security</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full">
                <FaUsers className="text-green-400" />
                <span>Multi-Platform</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full">
                <FaChartLine className="text-green-400" />
                <span>Real-Time Analytics</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-6 sm:py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 text-sm sm:text-base ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LearnMore; 